// Attendance (/attendance) — where the field team is right now and the hours
// behind every shift. The page owns only the header + settings drawer (which
// also holds the API-keys entry point) and the tab strip (local state, like
// every tabbed page); each tab mounts only while active and owns its own
// requests, so landing on one never fires the other's:
//   Overview — overview/OverviewTab: today's summary tiles, the punch-record
//     table with its Filter dropdown, and the row-click detail drawer.
//   Live tracking — live-tracking/LiveTrackingTab: the linked users'
//     live-locations card (its socket connects on mount).
//
// Permissions: the module grants only `view` + `export`. The module grant
// itself (nav/route gating on moduleSlug 'attendance') IS the view gate for
// everything here — including Settings, deliberately: no settings verb exists
// and none is invented, the backend enforces the write. The `export` verb has
// no UI yet; its named hook joins useModuleAccess.ts when a feature arrives.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tabs } from '@/components/common';
import {
  DEFAULT_ATTENDANCE_SETTINGS,
  useAddAttendanceSettingMutation,
  useGetAttendanceSettingsQuery,
  useUpdateAttendanceSettingsMutation,
} from '@/api/endpoints/generalSettingsApi';
import OverviewTab from './overview/OverviewTab';
import LiveTrackingTab from './live-tracking/LiveTrackingTab';
import AttendanceSettingsDrawer, { type AttendanceSettings } from './AttendanceSettingsDrawer';

type AttendanceTab = 'overview' | 'live';

const AttendancePage = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<AttendanceTab>('overview');
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Settings — fetched once; the save branches on the fetched id (null =
  // never created → POST add; id → PATCH update). The drawer drafts locally
  // and persists on Save changes; its model IS the save body.
  const { data: settingsData, isError: settingsFetchError } = useGetAttendanceSettingsQuery();
  const [addSettings, addState] = useAddAttendanceSettingMutation();
  const [updateSettings, updateState] = useUpdateAttendanceSettingsMutation();
  const settingsValue: AttendanceSettings = settingsData ?? DEFAULT_ATTENDANCE_SETTINGS;

  const saveSettings = async (next: AttendanceSettings) => {
    // Never save blind: without the fetched row the id is unknown, and the
    // create/update branch could mis-create for a company whose settings
    // exist. Save is disabled until the fetch succeeds (ready prop).
    if (!settingsData) return;
    try {
      if (settingsData.id) await updateSettings({ id: settingsData.id, ...next }).unwrap();
      else await addSettings(next).unwrap();
      setSettingsOpen(false);
    } catch {
      // The mutation's isError drives the drawer's inline alert; it stays open.
    }
  };

  return (
    <div className='attendance-page'>
      <header className='attendance-page__header'>
        <div>
          <h1 className='attendance-page__title'>{t('attendance_title')}</h1>
          <p className='attendance-page__subtitle'>{t('attendance_subtitle')}</p>
        </div>
        <div className='attendance-page__header-actions'>
          <Button
            variant='secondary'
            iconStart='settings'
            onClick={() => {
              // A stale failure from the last session shouldn't greet the next.
              addState.reset();
              updateState.reset();
              setSettingsOpen(true);
            }}
          >
            {t('attendance_settings_button')}
          </Button>
        </div>
      </header>

      <Tabs
        value={tab}
        onChange={(v) => setTab(v as AttendanceTab)}
        aria-label={t('attendance_title')}
        items={[
          { value: 'overview', label: t('attendance_tab_overview'), icon: 'layout-grid' },
          { value: 'live', label: t('attendance_tab_live'), icon: 'map-pin' },
        ]}
      />

      {/* One tab mounted at a time: its queries (and the Live tab's socket)
          start on mount and stop on unmount; its local state resets with it. */}
      {tab === 'overview' && <OverviewTab />}
      {tab === 'live' && <LiveTrackingTab />}

      <AttendanceSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        value={settingsValue}
        onSave={saveSettings}
        ready={Boolean(settingsData)}
        saving={addState.isLoading || updateState.isLoading}
        error={addState.isError || updateState.isError || settingsFetchError}
      />
    </div>
  );
};

export default AttendancePage;
